 import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ApplicableTableSettings, codesAnTitleList } from '../../../tableSettings';

import useFastStorage from '../../../hooks/useFastStorage';
import useFunction from '../../../hooks/useFunction';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';

import ApplicationContext from '../../../ApplicationContext';
import useSystemSettings from '../../../hooks/useSystemSettings';
import { Search } from '../../../filtering';
import { JobNames } from '../../../framework.core/JobNames';
import { CodeANTitleListItem } from '../../../fms.data/models/CodeANTitleListItem';
import { SQLType } from '../../../framework.core/data/SQLInfo';
import { ApiResult, InsertResult } from '../../../framework.core/data/ApiResult';
import { Portal } from 'react-portal';
import TextBox from '../../../ui/TextBox';
import EditableTable, { CellData, EditableTableHandle, TableCellTypes } from '../../../ui/tables/editing/EditableTable';
import { FmsSecTask } from '../../../fms.core/constants';
import GreenButton from '../../../ui/GreenButton';
import { checkTabIsDirty } from '../ReferenceFiles';
import { findSecurityTask } from '../../../util';

import BlackInverseButton from '../../../ui/BlackInverseButton';
import constants from '../../../constants';
import IFmsReferenceFilesContext from '../IfmsReferenceFilesContext';
import CloseButton from '../../../ui/CloseButton';
import useFunctionKeys from '../../../hooks/useFunctionKeys';

function AnTitleRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`antitle-${row.index}`} value={l.antitle} maxLength={100} onChange={e => changeLineItem(row.index, {antitle: e.target.value, isUpdate:true})} />
  );
}

function formatElement(data: any) {
  const result = new CodeANTitleListItem();
  result.antitle = data.antitle;
  result.antitle1 = data.antitle1;
  result.antitle1Alt = data.antitle1Alt;
  result.antitle2 = data.antitle2;
  result.antitle2Alt = data.antitle2Alt;
  result.cid = data.cid;
  result.isNew = false;

  return data;
}

function formatData(data:Array<any>) {
  return data.map(d => formatElement(d));
}

function AnTitles() {
  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
    //ifmsReferenceFilesCancel: () => {},
  }});
  useFunctionKeys({ onSave: handleSaveAndClose });

  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const tableRef = useRef(null);
  const masterTableRef = useRef<EditableTableHandle>(null);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState(Array<String>());
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, /*dataRef*/] = useFastStorage(`ifms-referenceFiles-antitle-list`, []);

  const savedDataRef = useRef<Array<CodeANTitleListItem>>([]);
  const oriData = useRef<CodeANTitleListItem[]>([]);
  //const addingToTheFirstLine = useRef(false);

  const listSec = findSecurityTask(appContext, FmsSecTask.CodeANTitle);
  const [focusFirstCell, setFocusFirstCell] = useState(true);
  
  const tableHeight = 520;
  const systemSettings = useSystemSettings();

  const listItemType = 'CodeANTitleListItem';

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelector('[class^="ReactModal__Content"]');
      if (!modals)
        saveBtn.click();
    }
  }

  function cancelOperation() {
    setHasChanges(false);
    removeData();
    return true;
  }
  
  function setData(d:Array<any>, callByFetchData: boolean = false) {
    setFocusFirstCell(callByFetchData);
    _setData(d, null, { forceClone: callByFetchData });
    setHasChanges(checkTabIsDirty(CodeANTitleListItem, oriData.current, d));
  }

  async function fetchData(api:any) {
    setLoading(true);
    //<pt220818: portal
    // const apiResult = await api.getCodesAnTitles();
    // setData(formatData(apiResult || []));
    const search = { pageSize: systemSettings.defaultPageSize} as Search;
    const apiResult = await api.getListItems(JobNames.ANTitle,
      listItemType,
      CodeANTitleListItem.calculateSearchParameters(search), 1, search.pageSize);
    const apiResultforAll = await api.getListItems(JobNames.ANTitle,
      listItemType,
      CodeANTitleListItem.calculateSearchParameters(search),
      1);
    oriData.current = formatData(apiResult && apiResult.entities ? apiResult.entities : []);  
    setData(formatData(apiResult && apiResult.entities ? apiResult.entities : []), true);

    savedDataRef.current = apiResult && apiResult.entities ? apiResult.entities : [];
    if(apiResultforAll && apiResultforAll.entities){
      setAllData(apiResultforAll.entities.map((a: CodeANTitleListItem) => a.antitle.toLowerCase()));
    }
    //pt220818>

    setFocusFirstCell(false);
    setLoading(false);
    setHasChanges(false);
  }

  useEffect(() => {
    if (data.length === 0)
      reload();
    else {
      oriData.current = _.cloneDeep(data);
      savedDataRef.current = _.cloneDeep(data);
    }
    return () => {
      removeData();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function reload() {
    fetchData(appContext.api);
  }

  function getIndexOfFirstInvalidCell(rowIndex: number):number|undefined {
    if (!data) {
      return undefined;
    }

    if (!data[rowIndex].antitle || data[rowIndex].antitle.trim().length === 0 ) {
      return 1;
    }
    if (allData) { //기존데이터 목록
      const newData = [...allData];
      if(newData.filter(x => x === data[rowIndex].antitle.toLowerCase()).length === 0) // 현재 목록에 없음
      {
        newData.splice(rowIndex, 1, data[rowIndex].antitle.toLowerCase());
        setAllData(newData);
        return undefined;
      }else{
        if(newData[rowIndex] === data[rowIndex].antitle.toLowerCase()) return undefined;
        else{
          showGlobalOkDialog(<p>'{data[rowIndex].antitle}' is a duplicate value.</p>);
          return 1;
        }
      }
    } 
    else {
      const newData = [data[rowIndex].antitle.toLowerCase()];
      setAllData(newData);
    }
    return undefined;
  }

  function deleteRow(index: number): boolean {
    if (!data) {
      return false;
    }

    const element = data[index];

    if (!element.isNew) {
      let deleteRow = savedDataRef.current.find(x => x.antitle === element.antitle);
      if (deleteRow) {
        deleteRow.isDelete = true;
      }
    }
    data.splice(index, 1);
    setData(data);
    const tempData = data.map((a: CodeANTitleListItem) => a.antitle.toLowerCase());
    const newData = allData.splice(0, tempData.length, tempData);
    setAllData(newData);

    return true;
  }

  function applyToLineItem(rowIndex: number, change: Partial<CodeANTitleListItem>|null) {
    if (!change || !data) {
      return;
    }
    const checkData = data[rowIndex];
    if (!change.isNew && (change.antitle !== checkData.antitle || change.antitle1 !== checkData.antitle1 ||
      change.antitle2 !== checkData.antitle2)) {
      _.merge(data[rowIndex], { ..._.omit(change, ['isNew']), hasBeenChanged: true });
      setHasChanges(true);
      setData(data);
    }
  }


  // function addingLineToFirstLine(){
  //   addingToTheFirstLine.current = true;
  //   masterTableRef.current?.add();
  // }

  function addLineItem() {
    const newItem = new CodeANTitleListItem();
    newItem.antitle = '';
    newItem.antitle1 = false;
    newItem.antitle1Alt = false;
    newItem.antitle2 = false;
    newItem.antitle2Alt = false;
    newItem.cid = '';
    newItem.isNew = true;

    setHasChanges(true);

    return {
      setDataFunc: (list: any) => setData(list),
      curListData: data,
      newData: newItem
    };
  }

  async function duplicateCell(checkData: any) {
    if (!data) {
      return;
    }

    let duplicateFieldText = '';

    let isDuplicate = false;
    let insList = checkData.filter((x: any) => x.isNew === true);
    let chgList = checkData.filter((x: any) => x.hasBeenChanged === true && !x.isNew);
    let focusRow = -1;

    if (insList.length > 0 && data.length > 0) {
      for (let i = 0; i < insList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (insList[i].antitle.toUpperCase() === data[j].antitle.toUpperCase()) {
            if (count === 0) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${insList[i].antitle}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (chgList.length > 0 && data.length > 0) {
      for (let i = 0; i < chgList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (chgList[i].antitle.toUpperCase() === data[j].antitle.toUpperCase()) {
            if (!data[j].isNew && data[j].hasBeenChanged === true && tempRow === -1) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${chgList[i].antitle}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (!isDuplicate) {
      const apiResult = await appContext.api.checkDuplicatedKeys(JobNames.ANTitle, listItemType, insList, chgList);
      if (apiResult && apiResult.success && apiResult.message) {
        duplicateFieldText = `${apiResult.message}`;
        isDuplicate = true;
      } else if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog('Could not check duplication.\r\nPlease try later.');
        return true;
      }
    }
    else
      await showGlobalOkDialog(`${duplicateFieldText}Please try after correcting duplication.`);

    return isDuplicate;
  }

  async function save() {
    if (!data) {
      return;
    }

    let validData = data.filter((x:any) => x.antitle);

    let errMsg = '';
    const dataToSave = validData.filter((x: any) => x.isNew || x.hasBeenChanged === true); // new data is always last so index check works
    const dataToDelete = savedDataRef.current.filter((x) => x.isDelete);
    if(dataToSave.length === 0 && dataToDelete.length === 0) {
      if (hasChanges) reload();
      return;
    }
    setLoading(true);

    for (let i = 0; i < dataToDelete.length; ++i) {
      const s = dataToDelete[i];
      const apiResult: ApiResult<InsertResult> = await appContext.api.saveListItem(listItemType,
        SQLType.Delete,
        _.omit(s, ['isDelete']));

      if (!apiResult || !apiResult.success)
        errMsg = apiResult.message ?? constants.SAVE_ERROR_MESSAGE;
    }

    if (dataToSave.length > 0) {
      const isDuplicate = await duplicateCell(dataToSave);
      if(isDuplicate){
        setLoading(false);
        return;
      }
      const cidData = dataToSave.map((a: CodeANTitleListItem) => a.cid?.toLowerCase()) as Array<String>;

      const secondSave = dataToSave.filter((x: CodeANTitleListItem) => cidData.includes(x.antitle.toLowerCase()));
      const firstSave = dataToSave.filter((x: CodeANTitleListItem) => !secondSave.includes(x));

      if (firstSave.length > 0) {
        const apiResult: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, firstSave);
        if (!apiResult || !apiResult.success) {
          errMsg += (errMsg ? '\n' : '') + apiResult?.message;
        }
        else {
          firstSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.antitle === x.antitle);
            if (index >= 0) {
              data[index].cid = x.antitle;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }
      if (secondSave.length > 0) {
        const apiResult: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, secondSave);
        if (!apiResult || !apiResult.success) {
          errMsg += (errMsg ? '\n' : '') + apiResult?.message;
        }
        else {
          secondSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.antitle === x.antitle);
            if (index >= 0) {
              data[index].cid = x.antitle;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              delete data[index].isNew;
            }
          })
        }
      }
    }
    if (errMsg) {
      await showGlobalOkDialog(<p>{errMsg}</p>);
      setLoading(false);
      return;
    }

    // setData(data);
    // savedDataRef.current = _.cloneDeep(data);
    // oriData.current = _.cloneDeep(data);
    
    // setLoading(false);
    // setHasChanges(false);
    reload();

    await showGlobalOkDialog(<p>The data has been saved</p>);
  }

  function cellValueChanged(rowIndex: number, accessor: string, value: any) {
    switch (accessor) {
      case 'antitle1':
        applyToLineItem(rowIndex, {antitle1: value, isUpdate:true});
        break;
      case 'antitle2':
        applyToLineItem(rowIndex, { antitle2: value, isUpdate:true });
        break;
    }
  }


  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-antitle-list',
    ref: tableRef,
    tableName: 'anTitleList',
    ...codesAnTitleList,
    height: `${tableHeight}px`,
    data: data.map((x:any) => ({...x, changeLineItem: applyToLineItem})),
    rowRenderers: [
      { accessor: 'indicator' },
      { accessor: 'antitle',  EditCell: AnTitleRenderer },
      { accessor: 'antitle1' },
      { accessor: 'antitle2'  },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      if (accessor === 'antitle')
        return { type: TableCellTypes.Text,  value: rowData.antitle };
      else if (accessor === 'antitle1')
        return { type: TableCellTypes.Checkbox, value: rowData.antitle1 , onChangeFunc: (antitle1: boolean) => cellValueChanged(rowIndex, accessor, antitle1) };
      else if (accessor === 'antitle2')
        return { type: TableCellTypes.Checkbox, value: rowData.antitle2, onChangeFunc: (antitle2: boolean) => cellValueChanged(rowIndex, accessor, antitle2) };
      else
        return undefined;
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      if(!(listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel))
        return true
      else
        return false;
    },
    useEditControls: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
  };

  //const AnTitleCount = data ? data.length : 0;
  return (
    <div style={{ marginTop: '40px' }}>
      {/* <Portal node={document && document.getElementById('settings-placeholder')}>
        <h1>Arrival Notice Title</h1>
      </Portal> */}

      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', marginTop:'5px'}}>
          <div className="ifms-common-bar-element">
            <GreenButton size="regular" disabled={loading} onClick={save}>Save</GreenButton>
          </div>
          <div className="ifms-common-bar-element ifms-left-separator-light-gray">
            <CloseButton name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
          {/* <div className="ifms-common-bar-element">
            <BlackInverseButton name='list-screen-refresh' size="large" style={{marginTop: '-5px'}} disabled={loading} onClick={reload} data-tooltip="Refresh" >
              <FontAwesomeIcon color='#47C83E' icon={faSync} />
            </BlackInverseButton>
          </div> */}
        </div>
      </Portal>

      <div style={{ display: 'flex', width: '864px' }}>
        <div style={{ marginLeft: 'auto' }}></div>

        <div style={{ position: 'relative', top: '-4px' }}>
          {//<SecondaryButton name='codes-antitle-add-button' size="regular" disabled={!listSec?.grantAdd} onClick={() => { addingLineToFirstLine() }}><FontAwesomeIcon icon={faPlus} />&nbsp; Add Line</SecondaryButton> &nbsp; 
          }
          <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { reload() }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton>
        </div>
      </div>
      <div style={{ width: '864px' }}>
        <EditableTable
          initializeFocusedCell={focusFirstCell}
          ref={masterTableRef}
          tableOptions={tableOptions}
          deleteLine={deleteRow}
          addLine={addLineItem}
          getIndexOfFirstInvalidCell={getIndexOfFirstInvalidCell}
          canDeleteRow={rowNumber => listSec?.grantDel ?? false}
        />
      </div>
    </div>
  );
}

export default AnTitles;